import { getBaseloadAndCO2Chart } from "$/api/market-monitoring/other-markets.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import ComposedLineGraph from "$/lib/components/graphs/ComposedLineGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const COLORS = ["#234078", "#2e6b34", "#d5d6d6"];

const BaseloadAndCO2Chart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-other-markets-baseload-co2-chart", dateRange],
    queryFn: () =>
      getBaseloadAndCO2Chart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const toDisplay = useMemo(() => data ?? [], [data]);

  const keys = useMemo(() => ["Baseload", "PEG", "CO2"], []);

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]: COLORS[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        color: COLORS[i],
        isVisible: true,
      })),
    );
  }, [keys]);

  return (
    <DisplayBox
      title="Base Cal+1 et PEG Cal+1 vs. EUA Dec +1"
      containerClassName={cn("shadow-displayBoxShadow !py-2", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-[520px] py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <ComposedLineGraph
            data={toDisplay}
            xAxisDataKey="date"
            keys={keys}
            colors={colors}
            graphVisibility={graphVisibility}
            theme="orange"
            secondYAxis={{
              type: "AREA",
              dataKey: "CO2",
              label: "CO2",
              unit: " €/t",
              color: COLORS[2] ?? "#d5d6d6",
            }}
            keysToFilterFromDomain={["CO2"]}
            connectNulls
            rightMargin={0}
          />
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="orange"
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default BaseloadAndCO2Chart;
